import { create } from 'zustand';
import { authAPI } from './api';

const getAuthErrorMessage = (error, fallback) => {
  if (error?.code === 'API_URL_MISSING') {
    return 'Backend URL not configured. Contact support or reinstall with production settings.';
  }

  if (error?.code === 'ECONNABORTED') {
    return 'Backend timeout. Please try again in a moment.';
  }

  if (!error?.response) {
    return 'Backend unreachable. Check your connection or try again later.';
  }

  return error.response?.data?.detail || fallback;
};

export const useAuthStore = create((set, get) => ({
  user: JSON.parse(localStorage.getItem('rtb_user') || 'null'),
  token: localStorage.getItem('rtb_token'),
  isAuthenticated: !!localStorage.getItem('rtb_token'),
  isLoading: false,
  error: null,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user } = response.data;
      
      localStorage.setItem('rtb_token', access_token);
      localStorage.setItem('rtb_user', JSON.stringify(user));
      
      set({
        user,
        token: access_token,
        isAuthenticated: true,
        isLoading: false,
      });
      
      return { success: true, user }; // FIXED: Include user in response
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Login failed');
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  register: async (data) => {
    set({ isLoading: false, error: null });
    return {
      success: false,
      error: 'Direct registration is disabled. Please use your invitation activation link.',
    };
  },

  logout: () => {
    localStorage.removeItem('rtb_token');
    localStorage.removeItem('rtb_user');
    localStorage.removeItem('is_logged_in');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
    });
  },

  refreshUser: async () => {
    try {
      const response = await authAPI.getMe();
      const user = response.data;
      localStorage.setItem('rtb_user', JSON.stringify(user));
      set({ user });
      return { success: true, user };
    } catch (error) {
      get().logout();
      return { success: false, error };
    }
  },
}));

// Role-based access helpers
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  OPS: 'ops',
  GM: 'gm',
  HEAD_CHEF: 'head_chef',
  MANAGER: 'manager',
  STAFF: 'staff',
};

export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 5,
  [ROLES.OPS]: 4,
  [ROLES.GM]: 3,
  [ROLES.HEAD_CHEF]: 2,
  [ROLES.MANAGER]: 2,
  [ROLES.STAFF]: 1,
};

export const hasRole = (user, allowedRoles) => {
  if (!user) return false;
  return allowedRoles.includes(user.role);
};

export const hasMinimumRole = (user, minimumRole) => {
  if (!user) return false;
  return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minimumRole];
};

export const getRoleLabel = (role) => {
  const labels = {
    [ROLES.SUPER_ADMIN]: 'Super Admin',
    [ROLES.OPS]: 'Operations Director',
    [ROLES.GM]: 'General Manager',
    [ROLES.HEAD_CHEF]: 'Head Chef',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.STAFF]: 'Staff',
  };
  return labels[role] || role;
};

export const getUserAccessState = (user) => {
  if (!user) return 'unauthenticated';
  if (user.is_active === false || user.status === 'disabled') return 'disabled';
  if (user.status === 'pending_activation' || user.first_login === true) return 'pending_activation';
  return 'active';
};

export const getDefaultRouteForUser = (user) => {
  if (!user) return '/login';
  return user.role === ROLES.STAFF ? '/staff-hub' : '/dashboard';
};
